/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  serverTimestamp,

} from "firebase/firestore";
import { db, auth } from "../../../firebase";
import { Absence } from "./absence.type";
import { handleNotifyAbsence } from "../../../services/firebaseService";

const parseDateValue = (value: any): number => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const date = new Date(value).getTime();
    return Number.isNaN(date) ? 0 : date;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  return 0;
};

const sortAbsencesByDateDesc = (absences: Absence[]) => {
  return absences.sort((a, b) => parseDateValue(b.date) - parseDateValue(a.date));
};

export const AbsenceApi = createApi({
  reducerPath: "absenceApi",
  tagTypes: ["absences", "absence", 'absenceByProf', 'absenceByStudent', 'absenceByTeacher', 'absencesStudent', 'notifyAbsence'],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    marquerAbsence: builder.mutation({
      async queryFn({ uid, ...data }) {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }
          const userId = user?.uid;
          const absenceData = Object.fromEntries(
            Object.entries(data).filter(([, value]) => value !== undefined),
          );

          if (!uid) {
            const docRef = await addDoc(collection(db, "absences"), { ...absenceData, createdAt: serverTimestamp(), createdBy: userId, updatedAt: serverTimestamp() });
            return {
              data: {
                uid: docRef.id,
                message: "Absence enregistrée !",
                ...absenceData,
              },
            };

          } else {
            // Mettre à jour Firestore
            const userRef = doc(db, "absences", uid);
            // si la reason est definie marquer justified a oui
            if (absenceData.reason) {
              absenceData.justified = "Oui";
            }

            await updateDoc(userRef, absenceData);

            return {
              data: {
                message:
                  "Absence modifiée",
              },
            };
          }
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["absence", "absences", "absenceByTeacher"],
    }),
    getAbsences: builder.query({
      queryFn: async () => ({ data: [] as Absence[] }),

      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          const q = query(collection(db, "absences"));

          unsubscribe = onSnapshot(q, (snapshot) => {
            const absences = snapshot.docs.map((doc) => ({
              uid: doc.id,
              ...doc.data(),
            })) as Absence[];

            updateCachedData(() => absences);
          }, (error) => {
            console.error("Erreur onSnapshot absences:", error);
          });

          await cacheEntryRemoved;
        } catch (error) {
          console.error("Erreur de souscription:", error);
        } finally {
          unsubscribe?.();
        }
      },
      providesTags: ["absence"],
    }),

    getAbsencesByProf: builder.query({
      queryFn: async () => ({ data: [] as Absence[] }),

      async onCacheEntryAdded(
        profId: string,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          const q = query(
            collection(db, "absences"),
            where("createdBy", "==", profId)
          );

          unsubscribe = onSnapshot(q, (snapshot) => {
            const absences = snapshot.docs.map((doc) => ({
              uid: doc.id,
              ...doc.data(),
            })) as Absence[];

            sortAbsencesByDateDesc(absences);
            updateCachedData(() => absences);
          }, (error) => {
            console.error("Erreur onSnapshot absencesByProf:", error);
          });

          await cacheEntryRemoved;
        } catch (error) {
          console.error("Erreur de souscription:", error);
        } finally {
          unsubscribe?.();
        }
      },
      providesTags: ["absenceByProf"],
    }),

    getAbsencesByStudent: builder.query({
      queryFn: async () => ({ data: [] as Absence[] }),

      async onCacheEntryAdded(
        studentId: string,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          const user = auth.currentUser;
          if (!user) {
            console.warn("Aucun utilisateur connecté.");
            return;
          }

          const q = query(
            collection(db, "absences"),
            where("createdBy", "==", user.uid),
            where("student.uid", "==", studentId)
          );

          unsubscribe = onSnapshot(q, (snapshot) => {
            const absences = snapshot.docs.map((doc) => ({
              uid: doc.id,
              ...doc.data(),
            })) as Absence[];

            sortAbsencesByDateDesc(absences);
            updateCachedData(() => absences);
          }, (error) => {
            console.error("Erreur onSnapshot:", error);
          });

          await cacheEntryRemoved;
        } catch (error) {
          console.error("Erreur de souscription:", error);
        } finally {
          unsubscribe?.();
        }
      },
      providesTags: ["absenceByStudent"],
    }),
    getAbsencesByTeacher: builder.query({
      queryFn: async () => ({ data: [] as Absence[] }),
      async onCacheEntryAdded(
        teacherId: string,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe: (() => void) | undefined;
        try {
          await cacheDataLoaded;
          if (!teacherId) return;
          const absencesQuery = query(
            collection(db, "absences"),
            where("teacher.uid", "==", teacherId),
          );
          unsubscribe = onSnapshot(absencesQuery, (snapshot) => {
            const absences = snapshot.docs.map((absenceDoc) => ({
              uid: absenceDoc.id,
              ...absenceDoc.data(),
            })) as Absence[];
            sortAbsencesByDateDesc(absences);
            updateCachedData(() => absences);
          });
          await cacheEntryRemoved;
        } catch (error) {
          console.error("Erreur de souscription aux absences du professeur:", error);
        } finally {
          unsubscribe?.();
        }
      },
      providesTags: ["absenceByTeacher"],
    }),
    getAbsenceById: builder.query({
      queryFn: async (absenceId) => {
        try {
          const absenceRef = doc(db, "absences", absenceId);
          const absenceSnap = await getDoc(absenceRef);

          if (!absenceSnap.exists()) {
            return {
              error: { status: 404, message: "Absence introuvable !" },
            };
          }

          return { data: { uid: absenceSnap.id, ...absenceSnap.data() } };
        } catch (error: any) {
          return { error: { status: 500, message: error.message } };
        }
      },
      providesTags: ["absence"],
    }),
    notifyAbsenceByEmail: builder.mutation({
      async queryFn(absenceId: string) {
        try {
          const response = await handleNotifyAbsence(absenceId);
          return { data: response };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["notifyAbsence"],
    }),
    // Supprimer
    deleteAbsence: builder.mutation({
      queryFn: async (id) => {

        try {
          const docRef = doc(db, "absences", id);
          await deleteDoc(docRef);
          return {
            data: { id, message: "Absence supprimée !", success: true },
          };
        } catch (error: any) {
          return { error: { status: "DELETE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["absence", "absenceByTeacher"],
    }),
    getAbsencesStudent: builder.query({
      queryFn: async (studentId: string) => ({ data: [] as Absence[] }),

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          const q = query(
            collection(db, "absences"),
            where("student.uid", "==", arg)
          );

          unsubscribe = onSnapshot(q, (snapshot) => {
            const absences = snapshot.docs.map((doc) => ({
              uid: doc.id,
              ...doc.data(),
            })) as Absence[];

            sortAbsencesByDateDesc(absences);
            updateCachedData(() => absences);
          }, (error) => {
            console.error("Erreur onSnapshot:", error);
          });

          await cacheEntryRemoved;
        } catch (error) {
          console.error("Erreur de souscription:", error);
        } finally {
          unsubscribe?.();
        }
      },
      providesTags: ["absencesStudent"],
    }),

  }),
});

export const {
  useMarquerAbsenceMutation,
  useNotifyAbsenceByEmailMutation,
  useDeleteAbsenceMutation,
  useLazyGetAbsenceByIdQuery,
  useGetAbsencesQuery,
  useGetAbsencesByProfQuery,
  useGetAbsencesByStudentQuery,
  useGetAbsencesByTeacherQuery,
  useGetAbsencesStudentQuery,
} = AbsenceApi;

